import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { schoolAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { ArrowLeft, Upload } from 'lucide-react';
import { toast } from 'sonner';

const SchoolSettings = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [school, setSchool] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    primary_color: '#C53030',
    secondary_color: '#9B2C2C',
  });

  useEffect(() => {
    if (user?.school_id) {
      loadSchool();
    }
  }, [user]);

  const loadSchool = async () => {
    try {
      const { data } = await schoolAPI.get(user.school_id);
      setSchool(data);
      setFormData({
        name: data.name,
        tagline: data.tagline || '',
        primary_color: data.primary_color,
        secondary_color: data.secondary_color,
      });
    } catch (error) {
      console.error('Failed to load school:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (user?.school_id) {
        await schoolAPI.update(user.school_id, formData);
        toast.success('School profile updated');
      } else {
        const { data } = await schoolAPI.create(formData);
        updateUser({ ...user, school_id: data.school_id });
        toast.success('School profile created');
      }
      navigate('/dashboard');
    } catch (error) {
      toast.error('Failed to save school profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.school_id) return;

    try {
      await schoolAPI.uploadLogo(user.school_id, file);
      toast.success('Logo uploaded successfully');
      loadSchool();
    } catch (error) {
      toast.error('Failed to upload logo');
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button data-testid="back-button" variant="ghost" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold text-[#1C1917]">School Settings</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <Card className="p-8 border border-stone-200">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight text-[#1C1917] mb-6">
            {user?.school_id ? 'Update' : 'Create'} School Profile
          </h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name">School Name</Label>
              <Input
                id="name"
                data-testid="school-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="mt-1"
                placeholder="GD Goenka School East Delhi"
              />
            </div>

            <div>
              <Label htmlFor="tagline">Tagline (Optional)</Label>
              <Input
                id="tagline"
                data-testid="school-tagline-input"
                value={formData.tagline}
                onChange={(e) => setFormData({ ...formData, tagline: e.target.value })}
                className="mt-1"
                placeholder="Excellence in Education"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="primary_color">Primary Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="primary_color"
                    data-testid="primary-color-input"
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    value={formData.primary_color}
                    onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="secondary_color">Secondary Color</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="secondary_color"
                    data-testid="secondary-color-input"
                    type="color"
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="w-20 h-10"
                  />
                  <Input
                    value={formData.secondary_color}
                    onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            {user?.school_id && (
              <div>
                <Label>School Logo</Label>
                <div className="mt-2">
                  {school?.logo_url && (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${school.logo_url}`} alt="Logo" className="w-32 h-32 object-contain mb-4 border border-stone-200 rounded p-2" />
                  )}
                  <label htmlFor="logo-upload" className="cursor-pointer">
                    <div className="border-2 border-dashed border-stone-300 rounded-md p-6 hover:border-[#C53030] transition-colors text-center">
                      <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
                      <p className="text-sm text-stone-600">Click to upload logo</p>
                    </div>
                    <input
                      id="logo-upload"
                      data-testid="logo-upload-input"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}

            <Button
              data-testid="save-school-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#C53030] hover:bg-[#9B2C2C] text-white rounded-md"
            >
              {loading ? 'Saving...' : user?.school_id ? 'Update Profile' : 'Create Profile'}
            </Button>
          </form>
        </Card>
      </main>
    </div>
  );
};

export default SchoolSettings;